import { useAuth, authHeaders, tenantHeaders } from './auth-store'

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

async function call<T>(path: string, headers: HeadersInit, opts: RequestOpts = {}): Promise<T> {
  const { apiUrl } = useAuth.getState()
  const url = apiUrl + path
  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    credentials: 'include', // Better-Auth session cookie en modo owner
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...headers,
    },
    ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
    ...(opts.signal && { signal: opts.signal }),
  })
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const message = (data as { error?: { message?: string } })?.error?.message ?? `HTTP ${res.status}`
    throw new Error(message)
  }
  return data as T
}

/** GET que devuelve el body como string crudo (para CSV / blob downloads). */
async function callRaw(path: string, headers: HeadersInit, opts: RequestOpts = {}): Promise<string> {
  const { apiUrl } = useAuth.getState()
  const res = await fetch(apiUrl + path, {
    method: opts.method ?? 'GET',
    headers: { ...headers },
    ...(opts.signal && { signal: opts.signal }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.text()
}

/** /api/admin/* — uses adminToken (bootstrap or session). */
export const adminApi = {
  get: <T>(path: string, opts?: RequestOpts) => call<T>(path, authHeaders(), opts),
  post: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, authHeaders(), { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, authHeaders(), { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, authHeaders(), { ...opts, method: 'PUT', body }),
  delete: <T>(path: string, opts?: RequestOpts) =>
    call<T>(path, authHeaders(), { ...opts, method: 'DELETE' }),
}

/** /api/v1/* — uses activeTenantApiKey (each tenant's storefront API key). */
export const tenantApi = {
  get: <T>(path: string, opts?: RequestOpts) => call<T>(path, tenantHeaders(), opts),
  getRaw: (path: string, opts?: RequestOpts) => callRaw(path, tenantHeaders(), opts),
  post: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, tenantHeaders(), { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, tenantHeaders(), { ...opts, method: 'PATCH', body }),
  put: <T>(path: string, body: unknown, opts?: RequestOpts) =>
    call<T>(path, tenantHeaders(), { ...opts, method: 'PUT', body }),
  del: <T>(path: string, opts?: RequestOpts) =>
    call<T>(path, tenantHeaders(), { ...opts, method: 'DELETE' }),
  delete: <T>(path: string, opts?: RequestOpts) =>
    call<T>(path, tenantHeaders(), { ...opts, method: 'DELETE' }),
}
